/**
 * Hook that wires F8 card actions to web navigation + the chat send fn.
 * Mirrors the pattern in app/src/components/F8/F8MessageBubble.js.
 */

import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { handleCardAction, type CardAction, type CardActionResult } from '../utils/cardActionHandler';

const ACTION_DEBOUNCE_MS = 1500;

interface Options {
  /** Called when an action returns a `skill_invoke` result — caller forwards to sendMessage. */
  onSkillInvoke?: (message: string) => void;
  /** Called when the action triggers navigation; default is to use react-router navigate. */
  beforeNavigate?: () => void;
}

export function useCardActionHandler({ onSkillInvoke, beforeNavigate }: Options = {}) {
  const navigate = useNavigate();
  const lastActionAt = useRef(0);

  return useCallback(
    async (action: CardAction): Promise<CardActionResult> => {
      const now = Date.now();
      if (now - lastActionAt.current < ACTION_DEBOUNCE_MS) return null;
      lastActionAt.current = now;

      const result = await handleCardAction(action);
      if (!result) return null;

      if (result.type === 'navigate') {
        beforeNavigate?.();
        navigate(result.path, result.state ? { state: result.state } : undefined);
        return result;
      }

      if (result.type === 'skill_invoke' && result.message) {
        onSkillInvoke?.(result.message);
      }

      // Return cloud_function results so card components can inspect success/failure.
      return result;
    },
    [navigate, onSkillInvoke, beforeNavigate],
  );
}
